import { Config } from 'config';

import { forEach, assign, isEmpty,
         includes, tail, head, last } from 'lodash';

import { OwnedObject, isArray, flatMap, forwardPropertiesFrom,
         whenAll, resolve, map$, invokeMap$, imagePromise } from 'gear';

import { Xml } from '../util';
import { Point, Matrix, Bounds } from '../geometry';
import { Color, Brush, DisplayPen, MaskPen, EmptyBrush } from '../style';

import { downScaleQuantum$ } from '../figure/detail/quantum-image'; // [TODO]
import { Shape, Polyregion } from '../figure';

import { Layer, Layers, topTargetLayer } from './layer';
import { Page } from './page';
import { Document, registerDocumentType } from './document';


function filterIslandsByName( islands, name ) {
  const r = [];
  for( let k = 0, kEnd = islands.length; k < kEnd; ++k ) {
    const island = islands[k];
    if( textureIslandName(island) === name ) {
      r.push( island );
    }
  }
  return r;
}

function changeTextureIslandsName( islands, oldName, newName ) {
  forEach( islands, island => {
    if( textureIslandName(island) === oldName ) {
      island.metadata.set('textureIslandName',newName);
    }
  });
}

export function filterIslandsFor( islands, targetIsland ) {
  return filterIslandsByName( islands, textureIslandName(targetIsland) );
}

function findIslandByName( islands, name ) {
  for( let k = 0, kEnd = islands.length; k < kEnd; ++k ) {
    const island = islands[k];
    if( textureIslandName(island) === name ) {
      return island;
    }
  }
}

function findIslandFor( islands, targetIsland ) {
  return findIslandByName( islands, textureIslandName(targetIsland) );
}

function findLastIslandByName( islands, name ) {
  for( let k = islands.length-1; k >= 0; --k ) {
    const island = islands[k];
    if( textureIslandName(island) === name ) {
      return island;
    }
  }
}

/*
function findLastIslandFor( islands, targetIsland ) {
  return findLastIslandByName( islands, textureIslandName(targetIsland) );
}
*/

function removeChildByTag( node, tag ) {
  return node.removeChild( Xml.child(node,tag) );
}

export function textureIslandName(island) {
  return island.metadata.get('textureIslandName');
}

export function isTextureIsland( figure ) {
  return !! textureIslandName(figure);
}

export function isTextureIslandWithName( name ) {
  return figure => name === textureIslandName(figure);
}
export const isDocIslandWithName = isTextureIslandWithName;
export const isModelIslandWithName = isTextureIslandWithName;

// find( figures, isDocIslandWithName(name) )
// filter( figures, isDocIslandWithName(name) )

export function isTextureIslandFor( modelIsland ) {
  return isDocIslandWithName( textureIslandName(modelIsland) );
}
export const isDocIslandFor = isTextureIslandFor;
export const isModelIslandFor = isTextureIslandFor;

// find( figures, isDocIslandFor(textureIsland) )
// filter( figures, isDocIslandFor(textureIsland) )

export function newTextureIslandsLayer( islands ) {
  return new Layer({ figures: islands, xType: 'System.TextureIslands' });
}


function isBleedTextureIslandLayer( { xType } ) {
  return xType === 'System.TextureIslands_Bleed'   ||
         xType === 'System.OffsetedTextureIslands'  ; // Backward compatibility
}

function isCutTextureIslandLayer( { xType } ) {
  return xType === 'System.TextureIslands_Cut';
}

function isSafetyTextureIslandLayer( { xType } ) {
  return xType === 'System.TextureIslands_Safety' ||
         xType === 'System.TextureIslands';
}


export function docIslandsLayerFrom( page ) {
  return head( page.layers );
}

export function docIslandsFrom( page ) {
  return docIslandsLayerFrom(page).figures;
}

export function designLayersFrom( page ) {
  return tail( page.layers );
}

export function designFiguresFrom( page ) {
  return Layers.flatten( designLayersFrom(page) );
}


export function docToModelMatrix( docIsland, modelIsland ) {
  return Matrix.m( modelIsland.matrix_(), docIsland.matrix_().inverted() );
}

export function modelToDocMatrix( docIsland, modelIsland ) {
  return Matrix.m( docIsland.matrix_(), modelIsland.matrix_().inverted() );
}



export const ModelData3D = OwnedObject.extend({ typeName: 'ModelData3D',

  Properties: {
    data: { type: 'json', def: null, owned: false, onChange: function() {
      const owner = this._owner;
      if( owner ) {
        owner._id = null; // Model3D
      // owner.fireEvent('modeldata3dchanged'); [TODO], for the moment, doc.invalidateModelData3D() needs to be called
      }
      this._id = null;
    } },
    id: { type: 'string', def: null }
  },

  saveCopy$() {
    this.id = null;
    return this.save$();
  },
  save$() {
    if( this.id ) {
      // Do not overwrite
      return resolve(this);
    }
    let manager = this._resources || Config.GlobalResourceManager;
    return manager.saveModelData3D$(this);
  }
});

ModelData3D.load$ = function( id ) {
  return Config.GlobalResourceManager.loadModelData3D$(id);
};


const TextureMap3D_local = new Map();

const TextureMap3D_images = new Map();

const TextureMapCache = {

  cacheFile( file ) {
    const localId = file.name;
    TextureMap3D_local.set( localId, { file, id: null } );
    return localId;
  },

  isLocal( id ) {
    return TextureMap3D_local.has(id);
  },

  saveLocal$( id ) {
    const local = TextureMap3D_local.get(id);
    if( local.savePromise ) {
      return local.savePromise;
    }
    else if( local.file ) {
      const manager = Config.GlobalResourceManager;
      const promise = local.savePromise = manager.createTextureMap3D$(local.file) .then( textureMap => {
      local.file = null;
      local.savePromise = null;
      const id = local.id = textureMap.id;
      return id;
      });
      return promise;
    }
    else {
      resolve(local.id);
    }
  },

  loadImage$( id, size ) {
    let imagesMap = TextureMap3D_images.get(id);
    if( ! imagesMap ) {
      imagesMap = new Map();
      TextureMap3D_images.set(id,imagesMap);
    }
    let cache = imagesMap.get(size);
    if( cache === undefined ) {
      cache = {};
      imagesMap.set(size,cache);
    }
    if( cache.image ) {
      return resolve(cache.image);
    }
    else if( cache.promise ) {
      return cache.promise;
    }
    else {
      const local = TextureMap3D_local.get(id);
      if( local && local.file ) {
        const promise = cache.promise = imagePromise(URL.createObjectURL(local.file)) .then( rawImage => {
        if( rawImage.width > size ) {
          return downScaleQuantum$(rawImage,size) .then( image => {
          cache.image = image;
          cache.promise = null;
          return image;
          });
        }
        else {
          if( rawImage.width < size ) {
            console.log('TextureMap too small');
          }
          cache.image = rawImage;
          cache.promise = null;
          return rawImage;
        }

        });
        return promise;
      }
      else {
        const manager = Config.GlobalResourceManager;
        console.log('get '+id+ ' with size '+size);
        const promise = cache.promise = manager.renderTexture3D$({ id, size }) .then( imagePromise ) .then( image => {
        cache.image = image;
        cache.promise = null;
        return image;
        });
        return promise;
      }
    }
  },

  loadTextureTile$( id ) {
    const manager = Config.GlobalResourceManager;
    return manager.renderImage$({ id }) .then( imagePromise );
  }
};



export const TextureMap3D = OwnedObject.extend({ typeName: 'TextureMap3D',

  Properties: {
    id: { type: 'string', def: null },
    scale: { type: 'number', def: 1, dropDef: true },
    wrap: { type: 'string', def: 'Stretch', dropDef: true }, // 'Stretch' uses Texture3D store, 'Repeat' uses Image store
    repeat: { type: 'number', def: 1, dropDef: true }
  },

  init() {
    // Cache here the base64 representation

  },

  loadImage$( size ) {
    return TextureMapCache.loadImage$(this.id,size);
  },

  loadTextureTile$() {
    return TextureMapCache.loadTextureTile$(this.id);
  },

  save$() {
    if( TextureMapCache.isLocal(this.id) ) {
      console.log('uploading texture '+this.id);
      return TextureMapCache.saveLocal$(this.id) .then( id => {
      this.id = id;
      return this;
      });
    }
    else {
      return resolve(this);
    }
  }
});

Xml.addDefaultParseXml(TextureMap3D);
Xml.addDefaultSetupXml(TextureMap3D);


TextureMap3D.fromFile = function( file ) {
  const localId = TextureMapCache.cacheFile(file);
  return new TextureMap3D({ id: localId });
};



const textureMaps = [ 'colorMap', 'specularMap', 'normalMap', 'bumpMap', 'displacementMap', 'alphaMap' ];

function createModelIsland(name,polyregion) {
  const shape = Shape.createFromPolyregion(polyregion);
  shape.metadata.set('textureIslandName',name);
  return shape;
}

function onTextureChange() {
  const { document } = this;
  if( document ) {
    document.fireEvent('model3dchanged');
    document._id = null;
  }
}

export const Material3D_Props = OwnedObject.extend({ typeName: 'Material3D_Props',
  Properties: {
    colorMap        : { type: 'TextureMap3D', def: null, manualParsing: true, onChange: onTextureChange },
    specularMap     : { type: 'TextureMap3D', def: null, manualParsing: true, onChange: onTextureChange },
    normalMap       : { type: 'TextureMap3D', def: null, manualParsing: true, onChange: onTextureChange },
    bumpMap         : { type: 'TextureMap3D', def: null, manualParsing: true, onChange: onTextureChange },
    alphaMap        : { type: 'TextureMap3D', def: null, manualParsing: true, onChange: onTextureChange },
    displacementMap : { type: 'TextureMap3D', def: null, manualParsing: true, onChange: onTextureChange },
    textureWidth    : { type: 'number', def: 2048, onChange: onTextureChange },
    textureHeight   : { type: 'number', def: 2048, onChange: onTextureChange },
    backSide        : { type: 'string', def: 'Solid', dropDef: true, onChange: onTextureChange }
  },

  getTextureMap( textureType ) {
    return this[ textureType + 'Map' ];
  },

  getFixedTextureMap( textureType ) {
    return textureType === 'bump' ? ( this.normalMap ? null : this.bumpMap ) : this.getTextureMap(textureType);
  },

  buildXml( node ) {
    return Xml.addObject(node,this,'Material');
  },
  setupXml( node ) {
    Xml.setupProperties(node,this);
    forEach( textureMaps, map => {
      if( this[map] ) {
        const textureMapNode = Xml.appendElement(node,Xml.toXmlCase(map));
        this[map].setupXml(textureMapNode);
      }
    });
  },

  get document() {
    return this._owner.document;
  }
});

Material3D_Props.fromXml = function(node) {
  return Material3D_Props.parseXml(node); // Xml.getObject(node,Material3D_Props,'Material');
};
Material3D_Props.parseXml = function(node) {
  var p = Xml.parseProperties(node,Material3D_Props);
  forEach( Xml.children_(node), mapNode => {
    const nodeName = mapNode.nodeName;
    const name = Xml.toObjectCase(nodeName);
    if( includes(textureMaps,name) && ! ( name === 'normalMap' && ( Config.bumpMap || Config.tiledBumpMap || Config.tiledNormalMap ) ) ) {
      p[name] = TextureMap3D.parseXml(mapNode);
    }
  });

  return new Material3D_Props(p);
};

Xml.addFromBuild(Material3D_Props);

Material3D_Props.arrayFromXml = function( node ) {
  const materialsNode = Xml.child(node,'Materials');
  if( materialsNode ) {
    node = materialsNode;
  }
  return Xml.getObjectArray(node,Material3D_Props,'Material');
};

Material3D_Props.arrayBuildXml = function( node, v ) {
  if( isEmpty(v) ) {
    return;
  }
  const materialsNode = Xml.appendElement(node,'Materials');
  Xml.addObjectArray(materialsNode,v);
};

Xml.Parser['Material3D_Props'] = Xml.makeObjectParser(Material3D_Props);
Xml.Parser['Material3D_Props[]'] = Xml.makeArrayObjectParser(Material3D_Props);

export const Material3D = Page.extend(forwardPropertiesFrom(Material3D_Props,'properties',{ typeName: 'Material3D',

  Properties: {
    properties: { type: 'Material3D_Props', owned: true, def: new Material3D_Props(), manualParsing: true, onChange: function(){ } }
  },

  init(config) {
    this.callParent(config);
    if( this.layers.length === 1 ) {
      this.initModelIslandsLayers();
    }

    // Backward Compatibility, textureWidth, textureHeight at the layer level
    const layer = this.layers[0],
          { metadata } = layer,
          textureWidth  = parseFloat(metadata.get('textureWidth')),
          textureHeight = parseFloat(metadata.get('textureHeight'));
    if( textureWidth && textureHeight ) {
      this.properties._textureWidth  = textureWidth;
      this.properties._textureHeight = textureHeight;
    }
    metadata.delete('textureWidth');
    metadata.delete('textureHeight');
  },

  isDefault() {
    return false;
  },

  initModelIslandsLayers() {
    this.layers = [
      new Layer({ xType: 'System.TextureIslands_Cut' }),
      new Layer({ xType: 'System.TextureIslands_Safety' }),
      new Layer({ xType: 'System.TextureIslands_Bleed' })
    ];
  },

  addWholeModelIsland() {
    const bounds = new Bounds(0,0,this.textureWidth,this.textureHeight);
    const polyregion = Polyregion.CreateRectangle({ bounds,
      brush: new EmptyBrush(), pen: new DisplayPen(), fillMode: 'Alternate'
    });
    this.addModelIsland({ name: 'Texture',
      safety: polyregion, cut: polyregion, bleed: polyregion
    });
  },

  addModelIsland({ name, safety, cut, bleed }) {
    this.modelIslandsLayer.add( createModelIsland(name,safety) );
    this.cutModelIslandsLayer.add( createModelIsland(name,cut) );
    this.bleedModelIslandsLayer.add( createModelIsland(name,bleed) );
  },

  get modelIslandsLayer() {
    // return find( this.layers, isSafetyTextureIslandLayer );
    const { layers } = this;
    for( let i = 0, iEnd = layers.length; i < iEnd; ++i ) {
      const layer = layers[i];
      if( isSafetyTextureIslandLayer(layer) ) {
        return layer;
      }
    }
  },

  get cutModelIslandsLayer() {
    // return find( this.layers, isCutTextureIslandLayer );
    const { layers } = this;
    for( let i = 0, iEnd = layers.length; i < iEnd; ++i ) {
      const layer = layers[i];
      if( isCutTextureIslandLayer(layer) ) {
        return layer;
      }
    }
  },

  get bleedModelIslandsLayer() {
    // return find( this.layers, isBleedTextureIslandLayer );
    const { layers } = this;
    for( let i = 0, iEnd = layers.length; i < iEnd; ++i ) {
      const layer = layers[i];
      if( isBleedTextureIslandLayer(layer) ) {
        return layer;
      }
    }
  },

  get clipModelIslandsLayer() {
    return this.bleedModelIslandsLayer ||
           this.cutModelIslandsLayer ||
           this.modelIslandsLayer ;
  },

  // Most of Model3D should go here...

  getTextureMap( textureType ) {
    return this.properties.getTextureMap( textureType );
  },

  getFixedTextureMap( textureType ) {
    return this.properties.getFixedTextureMap( textureType );
  },

  setTextureMap( textureType, textureMap ) {
    this[ textureType + 'Map' ] = textureMap;
    const { document } = this;
    if( document ) {
      document.fireEvent('model3dchanged');
    }
  },

  setupXml( node ) {

    // BackwardCompatibility
    forEach( this.layers, ({ metadata }) => {
      metadata.set('textureWidth' ,this.textureWidth);
      metadata.set('textureHeight',this.textureHeight);
    });

    // const materialNode = Xml.appendElement(node,'Material');
    this.properties.setupXml(node);

    this.callParent(node);

    // BackwardCompatibility
    forEach( this.layers, ({ metadata }) => {
      metadata.delete('textureWidth');
      metadata.delete('textureHeight');
    });
  },

  save$() {
    return map$( textureMaps, map => {
      const textureMap = this[map];
      return textureMap && textureMap.save$();
    });
  }
}));

Material3D.arrayFromXml = function( node, bcFixes ) {
  return Page.arrayFromXml( node, bcFixes, Material3D );
};
Material3D.parseXml = function( node, bcFixes ) {
  const page = Page.parseXml( node, bcFixes, Material3D );
  page.properties = Material3D_Props.fromXml(node);
  return page;
};


Xml.addFromBuild(Material3D,'Material');
Xml.Parser['Material3D'] = Xml.makeObjectParser(Material3D);


export const Model3D = Document.extend({ typeName: 'Model3D',

  Properties: {
    modelData3D: { type: 'ModelData3D', owned: true, manualParsing: true, def: new ModelData3D() },
  },

  get materials() {
    return this.pages;
  },
  set materials( materials_ ) {
    this.pages = materials_;
  },

  get document3D() {
    // [TODO] We are going to ask for manual invalidation for the moment
    // doc3D.invalidateModel3D();
    // doc3D.invalidateModelData3d();
    return null; // this._owner;
  },

  /* [TODO]
  fireDoc3DEvent( eventName ) {
    const { document3D } = this;
    if( document3D ) {
      document3D.fireEvent(eventName,document3D,this);
    }
  },
  */

  init( config ) {

    this._loadingModelData3D = null;
    this.modelData3D_id = null;

    this.callParent(config);
    this._xType = 'System.Model3D';

    // We do not want history
    this.undoLimit = 0;
  },

  modelIslandWithName( name ) {
    return findIslandByName( this.modelIslands, name );
  },

  modelIslandFor( docIsland ) {
    return findIslandFor( this.modelIslands, docIsland );
  },

  bleedModelIslandWithName( name ) {
    return findIslandByName( this.bleedModelIslands, name );
  },

  bleedModelIslandFor( docIsland ) {
    return findIslandFor( this.bleedModelIslands, docIsland );
  },

  modelToDocMatrixFor( docIsland ) {
    const modelIsland = this.modelIslandFor(docIsland);
    return modelToDocMatrix(docIsland,modelIsland);
  },

  docToModelMatrixFor( docIsland ) {
    const modelIsland = this.modelIslandFor(docIsland);
    return docToModelMatrix(docIsland,modelIsland);
  },

  get modelIslandsLayer() {
    return this.materials[0].modelIslandsLayer;
  },

  get cutModelIslandsLayer() {
    return this.materials[0].cutModelIslandsLayer;
  },

  get bleedModelIslandsLayer() {
    return this.materials[0].bleedModelIslandsLayer;
  },

  get clipModelIslandsLayer() {
    return this.materials[0].clipModelIslandsLayer;
  },

  get modelIslands() {
    return this.modelIslandsLayer.figures;
  },

  get cutModelIslands() {
    return this.cutModelIslandsLayer.figures;
  },

  get bleedModelIslands() {
    return this.bleedModelIslandsLayer.figures;
  },

  get textureWidth() {
    return this.materials[0].textureWidth;
  },
  get textureHeight() {
    return this.materials[0].textureHeight;
  },

  changeIslandName( island, newName ) {
    const oldName = typeof island === 'string' ? island : textureIslandName(island);
    changeTextureIslandsName(this.modelIslands     ,oldName,newName);
    changeTextureIslandsName(this.cutModelIslands  ,oldName,newName);
    changeTextureIslandsName(this.bleedModelIslands,oldName,newName);
    this.id = null; // Force the creation of a new model3D
  },

  uvToModel( uv ) {
    return new Point( this.textureWidth * uv.x, this.textureHeight * uv.y );
  },

  modelIslandAt( modelPoint ) { // [CHECK]
    const { modelIslands } = this;
    for( let i = modelIslands.length-1; i >= 0; --i ) {
      const modelIsland = modelIslands[i];
      if( modelIsland.bounds_().contains( modelPoint ) ) {
        return modelIsland;
      }
    }
  },

  createCustomPage( name = 'Custom' ) {
    const modelIslandsLayer = this.modelIslandsLayer.clone();
    modelIslandsLayer.xType = 'System.TextureIslands';

    forEach( this.bleedModelIslands, bleedIsland => {

      const bleed = bleedIsland.polyregions[0].clone();
      bleed.pen   = new MaskPen();
      bleed.brush = new Brush({ color: Color.Transparent() });

      const island = findIslandFor( modelIslandsLayer.figures, bleedIsland );

      // The bleed mask goes under the safety
      island.polyregions.unshift( bleed );
    });

    return new Page({ name, layers: [
      modelIslandsLayer,
      new Layer() // Empty layer for the figures
    ]});
  },

  createDocument3D$() {
    const doc3D = new Document3D({
      pages: [ this.createCustomPage('Base') ],
      model3D: this,
      modelData3D: this.modelData3D
    });
    return resolve(doc3D);
  },

  save$() {
    const { modelData3D } = this;
    return modelData3D.save$() .then( () => {
    return invokeMap$(this.materials,'save$') .then( () => {
    let manager = this._resources || Config.GlobalResourceManager;
    return manager.saveModel3D$(this);
    }); });
  },

  loadModelData3D$() {
    if( this._loadingModelData3D ) {
      return this._loadingModelData3D;
    }
    else if( ! this.modelData3D_id ) {
      return this.modelData3D;
    }
    else {
      const promise = this._loadingModelData3D = ModelData3D.load$(this.modelData3D_id) .then( modelData3D => {
      this.modelData3D = modelData3D;
      this._loadingModelData3D = null;
      this.modelData3D_id = null;
      return modelData3D;
      });
      return promise;
    }
  },

  extraSetupProperties( docNode ) {
    const id = this.modelData3D_id || this.modelData3D.id;
    if( ! id ) {
      return console.log('No modelData3D id');
    }
    docNode.setAttribute('ModelData3D',id);

    // For the moment, set the material node in the Document if there is only
    // one material. This will be removed soon [TODO]
    if( this.materials.length === 1 ) {

      const pageNode = removeChildByTag(removeChildByTag(docNode,'Pages'),'Page');

      // docNode.appendChild( removeChildByTag(pageNode,'Material') );
      const materialNode = Xml.appendElement(docNode,'Material');
      this.materials[0].properties.setupXml(materialNode);
      docNode.appendChild( removeChildByTag(pageNode,'Layers') );
    }
  },
  extraXmlParsing( docNode ) {
    this.modelData3D_id = docNode.getAttribute('ModelData3D');

    // Backward compatibility, the <Material> node was at the doc level before
    const materialNode = Xml.child(docNode,'Material');
    if( materialNode ) {
      const material = this.materials[0];
      // TextureWidth, TextureHeight was not at the Material at that point
      const { textureWidth, textureHeight } = material;
      material.properties = Material3D_Props.parseXml(materialNode);
      assign( material, { textureWidth, textureHeight } );
    }

    forEach( textureMaps, map => {
      const forcedMap = Config[map];
      if( forcedMap ) {
        console.log('Forcing '+map+' with '+forcedMap);
        const scale = Config[map+'Scale'],
              repeat = Config[map+'Repeat'];
        this.materials[0][map] = TextureMap3D({
          id: forcedMap,
          scale: scale ? parseFloat(scale) : 1,
          wrap: Config[map+'Wrap'] || 'Stretch',
          repeat: repeat ? parseInt(repeat) : 1
        });
      }
    });
  }
});



Model3D.PageType = Material3D;

registerDocumentType('System.Model3D',Model3D);

Model3D.load$ = function( id ) {
  return Config.GlobalResourceManager.loadModel3D$(id);
};

Model3D.fromXml = function( o ) {
  const doc = Document.fromXml(o);
  return doc;
};

Model3D.fromXmlString = function( s ) {
  return Document.fromXmlString(s);
};




// Backward compatibility code, maybe it should be removed already
function applyBackwardCompatibleFixes( doc3D ) {
  forEach( doc3D.pages, page => {

    // If the islands layer is at the end, move it to the back
    const layers = page.layers;
    const figure = last( layers ).figures[0];
    if( figure && isTextureIsland(figure) ) {
      layers.unshift( layers.pop() );
    }

    docIslandsLayerFrom(page).forEachFigure( island => {
      const { polyregions } = island;
      if ( polyregions && Cx.isArray(polyregions) && polyregions.length > 0)
      {
        if( polyregions[0].pen instanceof DisplayPen ) {
          // Place the bleed island under the safety polyregion
          polyregions.unshift( polyregions.pop() );
        }

        // Make sure that the bleed island doens't have a transparent brush
        if( polyregions[0].brush._empty ) {
          polyregions[0].brush = new Brush({ color: Color.White() });
        }
      }
    });
  });
}



export const Document3D = Document.extend({ typeName: 'Document3D',

  Properties: {
    model3D: { type: 'Model3D', owned: false, def: new Model3D(), manualParsing: true, set(model) { this._setModel3D(model); } },
    materials: { type: 'Material3D_Props[]', owned: true, def: [ new Material3D_Props() ], onChange() { this.invalidateModel3D(); } }
  },

  save$(attr) {
    if( this.model3D.id ) {
      return this.callParent(attr);
    }
    else {
      return this.model3D.save$() .then( () => {
      return this.save$(attr);
      });
    }
  },

  get is3D() {
    return true;
  },

  get modelData3D() {
    return this.model3D.modelData3D;
  },

  invalidateMaterials() {
    this.fireEvent('materialschanged',this,this.model3D);
  },

  invalidateModel3D() {
    this.fireEvent('model3dchanged',this,this.model3D);
    // [TODO] Review
    this.invalidateModelData3D();
  },

  invalidateModelData3D() {
    this.fireEvent('modeldata3dchanged',this,this.modelData3D);
  },

  extraSetupProperties( docNode ) {

    const model3D_id = this.model3D_id || this.model3D.id;
    if( ! model3D_id ) {
      return console.log('No model3D id');
    }
    docNode.setAttribute('Model3D',model3D_id);

    const modelData3D_id = this.modelData3D_id || this.model3D.modelData3D_id || this.modelData3D.id;
    if( ! modelData3D_id ) {
      return console.log('No ModelData3D id');
    }
    docNode.setAttribute('ModelData3D',modelData3D_id);
  },

  init( config ) {

    this.callParent(config);
    this._xType = 'System.Document3D';
    this._docType = 'Doc3D';

    applyBackwardCompatibleFixes(this);
  },

  _setModel3D( model3D ) {
    this._model3D = model3D;
    this._model3D.id = model3D.id;
    // this.fireEvent('model3dchanged'); // [TODO]
  },

  // Overwrite add(), this simplify the creation of clients
  // because the normal addition pattern works as expected.
  add( figures ) {
    this._changeActiveLayerForAdding(figures);
    return this.callParent(figures);
  },

  _changeActiveLayerForAdding( figures ) {
    const f = isArray(figures) ? figures[0] : figures;
    const { activePage } = this;
    const islandsLayer = docIslandsLayerFrom(activePage);
    if( f._xType === 'System.TextureIsland' ) {
      this.activeLayer = islandsLayer;
    }
    else if( this.activeLayer === islandsLayer ) {
      const designLayers = designLayersFrom(activePage);
      const layer = topTargetLayer(designLayers);
      this.activeLayer = layer || last( designLayers );
    }
  },

  loadData3D$() {
    return this.loadModelData3D$();
  },

  loadModel3D$() {
    if( this._loadingModel3D ) {
      return this._loadingModel3D;
    }
    else if( ! this.model3D_id ) {
      return resolve(this.model3D);
    }
    else {
      const promise = this._loadingModel3D = Model3D.load$( this.model3D_id ) .then( model3D => {
      this.model3D = model3D;
      this.model3D_id = null;
      this.modelData3D_id = null;
      this._loadingModel3D = null;
      return model3D;
      });
      return promise;
    }
  },

  loadModelData3D$() {
    return this.loadModel3D$() .then( model => model.loadModelData3D$() );
  },

  // This function allows to copy the design of one size to other sizes
  // It is expected that the arrangements/views in both documents are similar
  copyDesignTo$( doc, config = {} ) {
    const me = this;
    const scaled = config.scaled === undefined ? false : config.scaled;
    return map$( me._pages, (pageFrom,pi) => {
      const pageTo = doc._pages[pi];
      if( pageTo ) {
        const islandsLayerFrom = docIslandsLayerFrom(pageFrom),
              islandsLayerTo   = docIslandsLayerFrom(pageTo);
        return whenAll([ islandsLayerFrom.bounds$(),
                         islandsLayerTo  .bounds$() ])
        .then( bounds => {
          const boundsFrom = bounds[0], boundsTo = bounds[1];
          let scale = 1.0;
          if( scaled ) {
            scale = Math.min( boundsTo.width / boundsFrom.width,
                              boundsTo.height / boundsFrom.height );
          }

          const layersFrom = designLayersFrom(pageFrom),
                layersTo   = designLayersFrom(pageTo);
          for(let li=0,liEnd=layersFrom.length;li<liEnd;++li) {
            const layerFrom = layersFrom[li],
                  layerTo   = layersTo[li];
            if( layerTo ) {
              // const islandsBaseDone = {};
              for(let fi=0,fiEnd=layerFrom._figures.length;fi<fiEnd;++fi) {
                const figureFrom = layerFrom._figures[fi];

                // Normal design figures
                const figure = figureFrom.clone();
                if( scaled ) {
                  figure.transform( Matrix.scale(scale) );
                }
                figure.pin = boundsTo.interpolate( boundsFrom.deinterpolate( figureFrom.pin ) );
                layerTo._figures.add( figure );
              }
            }
          }
        });
      }
    });
  },

  docIslandsFor( modelIsland ) {
    return flatMap( this.pages, page => filterIslandsFor( docIslandsFrom(page), modelIsland ) );
  },

  get topDocIsland() {
    return last( docIslandsFrom( last(this.pages) ) );
  },

  topDocIslandWithName( name ) {
    // [TODO] All pages should be considered, not only the last one
    return findLastIslandByName( docIslandsFrom( last(this.pages) ), name );
  },

  topDocIslandFor( modelIsland ) {
    return this.topDocIslandWithName( textureIslandName(modelIsland) );
  },

  extraXmlParsing( docNode ) {
    this.model3D_id = docNode.getAttribute('Model3D');
    this.modelData3D_id = docNode.getAttribute('ModelData3D');
  },

  changeIslandName( island, newName ) {
    const oldName = typeof island === 'string' ? island : textureIslandName(island);
    forEach( this.pages, page => {
      changeTextureIslandsName( docIslandsFrom(page), oldName, newName );
    });
    this.model3D.changeIslandName( oldName, newName );
  },

  clearDesignFigures() {
    return flatMap( this.pages, page => {
      return this.remove( designFiguresFrom(page) );
    });
  },

  // From apps, it is more clear to say doc3D.designFiguresFrom(page)
  designFiguresFrom( page ) {
    return designFiguresFrom(page);
  },
  docIslandsFrom( page ) {
    return docIslandsFrom(page);
  }
});

Document3D.load$ = function(config,options) {
  return Config.GlobalResourceManager.loadDocument3D$(config,options);
};

registerDocumentType('System.Document3D',Document3D);



/** WEBPACK FOOTER **
 ** ./src/cdl/document/document3d.js
 **/